import { RichTextEditor as MantineRichTextEditor, Link } from '@mantine/tiptap'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from 'tiptap-markdown'
import styled from 'styled-components'
import { Icon } from '../icon'
import { MessageInputProps } from './message-input.types'
import { Loader } from '../loader'
import '@mantine/tiptap/styles.css'
import {
  MarkdownTextAreaWrapper,
  StyledTextAreaContent,
  StyledTextAreaEditor,
  Divider,
} from '../markdown-text-area'

const SendButtonWrapper = styled.button`
  position: absolute;
  bottom: ${({ theme }) => theme.sizes[3]};
  right: ${({ theme }) => theme.sizes[4]};
  width: ${({ theme }) => theme.sizes[4]};
  height: ${({ theme }) => theme.sizes[4]};
  border: none;
  background: none;
  cursor: pointer;

  > svg {
    width: ${({ theme }) => theme.sizes[4]};
    height: ${({ theme }) => theme.sizes[4]};
    > path {
      fill: ${({ theme }) => theme.colors.primary400};
    }
  }

  > div > div > span {
    border-left-color: ${({ theme }) => theme.colors.primary400};
    border-top-color: ${({ theme }) => theme.colors.primary400};
  }

  &:disabled {
    > svg {
      > path {
        fill: ${({ theme }) => theme.colors.gray90};
      }
    }
  }
`

export function MessageInput({
  onSend,
  content = '',
  placeholder = 'Type a message...',
  isLoading = false,
  disabled = false,
  testId = 'message-input',
  submitOnEnter = true,
}: Readonly<MessageInputProps>) {
  const richTextEditor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        HTMLAttributes: {
          target: '_blank',
        },
      }),
      Highlight,
      Placeholder.configure({ placeholder }),
      Markdown,
    ],
    content,
    editorProps: {
      transformPastedHTML(html) {
        // Strip all HTML tags to allow only plain text
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = html
        return tempDiv.textContent || tempDiv.innerText || ''
      },
    },
  })

  const handleSend = () => {
    if (!richTextEditor) return

    const content = richTextEditor.storage.markdown.getMarkdown()
    // Handle escaped newlines
    const formattedContent = content.replace(/\\\n/g, '  \n')

    onSend(formattedContent)
    richTextEditor.commands.clearContent()
  }

  if (!richTextEditor) return null

  return (
    <MarkdownTextAreaWrapper data-testid={testId}>
      <StyledTextAreaEditor editor={richTextEditor}>
        <MantineRichTextEditor.Toolbar sticky>
          <MantineRichTextEditor.ControlsGroup>
            <MantineRichTextEditor.Bold />
            <MantineRichTextEditor.Italic />
            <MantineRichTextEditor.Underline />
            <MantineRichTextEditor.Strikethrough />
            <Divider />
            <MantineRichTextEditor.Link />
            <MantineRichTextEditor.Unlink />
          </MantineRichTextEditor.ControlsGroup>
        </MantineRichTextEditor.Toolbar>
        <StyledTextAreaContent
          data-testid="message-input-content"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && submitOnEnter) {
              e.preventDefault()
              handleSend()
            }
          }}
        />
      </StyledTextAreaEditor>

      <SendButtonWrapper
        disabled={disabled || !richTextEditor?.getText().length || isLoading}
        onClick={handleSend}
        data-testid="send-message-button"
      >
        {isLoading ? <Loader /> : <Icon name="send-message" />}
      </SendButtonWrapper>
    </MarkdownTextAreaWrapper>
  )
}
